import { Component, inject, signal } from '@angular/core';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { UserService } from '../../../../../services/user';
import { IUsers } from '../../../../../interfaces/user';
import { Router } from '@angular/router';
import { FlashService } from '../../../../../services/flash';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { Helpers } from '../../../../../utilities/helpers';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { DecimalPipe, NgClass } from '@angular/common';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { ISchools } from '../../../../../interfaces/school';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { AuthService } from '../../../../../services/auth';
import { DashboardService } from '../../../../../services/dashboard';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';
import { TeacherService } from '../../../../../services/teacher';

export interface IQualification {
  id: string;
  name: string;
}

@Component({
  selector: 'app-user-mgt-overview',
  imports: [
    Pagination,
    FormsModule,
    LoadingIndicator,
    NotFoundError,
    NgClass,
    ActionButton,
    ShimmerEffect,
    DecimalPipe,
  ],
  templateUrl: './user-mgt-overview.html',
  styleUrl: './user-mgt-overview.css',
})
export class UserMgtOverview {
  constructor(
    private userService: UserService,
    private router: Router,
    private session: Session,
    private schoolService: SchoolService,
    private authService: AuthService,
    private dashboardService: DashboardService,
    private teacherService: TeacherService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.loggedUsername = this.session.decodeToken(token)?.username;
    this.userSchoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    } else {
      this.getDashboardData();
    }

    this.getUsers();
  }

  protected schools = signal<ISchools | null>(null);
  protected loggedUserRole?: string;
  protected loggedUsername?: string;
  protected isLoading = signal<boolean>(false);
  protected users = signal<IUsers | null>(null);
  protected readonly flashService = inject(FlashService);
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected status: string = '';
  protected role: string = '';
  protected search: string = '';
  protected schoolId: string = '';
  protected changeRole = signal<boolean>(false);
  protected changeStatus = signal<boolean>(false);
  protected selectedUserName!: string;
  protected selectedUserId!: string;
  protected selectedUserStatus!: string;
  protected selectedUserRole!: string;
  protected newStatus: string = '';
  protected newRole: string = '';
  protected isEditing = signal<boolean>(false);
  protected validStatuses: string[] = ['ACTIVE', 'SUSPENDED', 'DEACTIVATED', 'PENDING'];
  protected validRoles: string[] = ['TEACHER', 'SCHOOL_ADMIN', 'FINANCE_ADMIN'];
  protected showAddUser = signal<boolean>(false);
  protected firstName: string = '';
  protected lastName: string = '';
  protected username: string = '';
  protected temporaryPassword: string = '';
  protected newUserRole: string = '';
  protected userSchoolId?: string;
  protected isSubmitting = signal<boolean>(false);
  protected showPassword: boolean = false;
  protected existingUsername = signal<boolean>(true);
  protected readonly Helpers = Helpers;
  protected dashboardData = signal<any>(null);
  protected staffNumber: string = '';
  protected hireDate: string = '';
  protected specialization: string = '';
  protected qualification: string = '';
  protected phone: string = '';
  protected employmentType: string = '';
  protected allowedEmploymentTypes = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY'];
  protected qualifications: IQualification[] = [];
  protected showResetPassword = signal<boolean>(false);
  protected isResetting = signal<boolean>(false);
  protected resetFirstName: string = '';
  protected resetLastName: string = '';
  protected resetUsername: string = '';
  protected resetRole: string = '';
  protected resetTemporaryPassword: string = '';
  protected resetShowPassword: boolean = false;

  protected getAvailableUsers(key: string): void {
    if (key === '') {
      return;
    }

    this.userService
      .getUserByUsername(
        `${ApiEndpoints.url}/user/username?school_id=${this.userSchoolId}&username=${key.toLowerCase()}`,
      )
      .subscribe({
        next: (data) => {
          if (data.payload.length) {
            this.existingUsername.set(true)
          } else {
            this.existingUsername.set(false)
          }
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Cannot verify username');
        },
      });
  }

  protected getUsers(filterType = this.currentFilterType, value = this.currentFilterValue): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.userService.getUsers(`${ApiEndpoints.url}/user`, params).subscribe({
      next: (data) => {
        this.users.set(data);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load users. Refresh the page');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getUsers();
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected selectUser(
    userId: string,
    action: string,
    name: string,
    status: string = '',
    role: string = '',
    user?: {
      id: string;
      first_name: string;
      last_name: string;
      username: string;
      role: string;
    },
  ): void {
    if (action === 'status') {
      this.changeStatus.set(true);
      this.selectedUserStatus = status;
    } else if (action === 'reset') {
      this.showResetPassword.set(true);
      this.resetTemporaryPassword = '';
      this.resetFirstName = user?.first_name ?? '';
      this.resetLastName = user?.last_name ?? '';
      this.resetUsername = user?.username ?? '';
      this.resetRole = user?.role ?? '';
      this.resetShowPassword = false;
    } else {
      this.changeRole.set(true);
      this.selectedUserRole = role;
    }

    this.selectedUserName = name;
    this.selectedUserId = userId;
  }

  protected editUser(form: NgForm, action: string) {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    let editForm;

    if (action === 'role') {
      editForm = { role: this.newRole };
    } else {
      editForm = { status: this.newStatus };
    }

    this.userService
      .editUser(`${ApiEndpoints.url}/user/${this.selectedUserId}/${action}`, editForm)
      .subscribe({
        next: () => {
          this.isEditing.set(false);
          this.flashService.success(`Successfully updated user ${action}`);
          this.changeRole.set(false);
          this.changeStatus.set(false);
          this.newStatus = '';
          this.newRole = '';
          this.getUsers();
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(
            `${err?.error?.message ?? `Failed to update user ${action}. Try again`}`,
          );
        },
      });
  }

  protected addQualification(): void {
    if (this.qualification.trim() === '') {
      this.flashService.failure('Please add a qualification');
      return;
    }

    const exists = this.qualifications.some(
      (qualification) => qualification.name === this.qualification,
    );

    if (exists) {
      this.flashService.failure('Cannot enter duplicate qualification');
      return;
    }

    const qualificationForm: IQualification = {
      id: crypto.randomUUID(),
      name: this.qualification,
    };

    if (this.qualification != '') {
      this.qualifications.push(qualificationForm);
    } else {
      this.flashService.failure('Please enter a qualification');
    }

    this.qualification = '';
  }

  protected deleteQualification(id: string): void {
    this.qualifications = this.qualifications.filter((i) => i.id !== id);
  }

  protected resetPassword(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    if (this.resetTemporaryPassword.trim().length < 8) {
      this.flashService.failure('Password must be at least 8 characters long');
      return;
    }

    this.isResetting.set(true);

    const resetPasswordForm = {
      user_id: this.selectedUserId,
      temporaryPassword: this.resetTemporaryPassword.trim(),
    };

    this.authService
      .resetPassword(`${ApiEndpoints.url}/auth/reset-password`, resetPasswordForm)
      .subscribe({
        next: () => {
          this.isResetting.set(false);
          this.flashService.success('Password reset successfully.');
          this.showResetPassword.set(false);
          this.resetTemporaryPassword = '';
          this.resetFirstName = '';
          this.resetLastName = '';
          this.resetUsername = '';
          this.resetRole = '';
          this.resetShowPassword = false;
          this.selectedUserId = '';
          this.selectedUserName = '';
          this.getUsers();
        },
        error: (err) => {
          this.isResetting.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Password reset failed. Try again.',
          );
        },
      });
  }

  protected signup(form: NgForm): void {
    this.isSubmitting.set(true);

    const username = this.username.toLowerCase();

    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    if (this.temporaryPassword.length < 8) {
      this.flashService.failure('Password must be at least 8 characters long');
      return;
    }

    const signupForm = {
      first_name: this.firstName.trim(),
      last_name: this.lastName.trim(),
      username: username.trim(),
      password: this.temporaryPassword.trim(),
      role: this.newUserRole,
      school_id: this.userSchoolId,
    };

    this.authService.signup(`${ApiEndpoints.url}/auth/signup`, signupForm).subscribe({
      next: (res) => {
        if (this.newUserRole === 'TEACHER') {
          const teacherForm = {
            school_id: this.userSchoolId,
            user_id: res.payload.id,
            staff_number: this.staffNumber,
            specialization: this.specialization,
            phone: this.phone,
            employment_type: this.employmentType,
            hire_date: this.hireDate,
            qualification: this.qualifications.map((q) => q.name),
          };

          this.teacherService.createTeacher(`${ApiEndpoints.url}/teacher`, teacherForm).subscribe({
            next: () => {
              this.isSubmitting.set(false);
              this.flashService.success('Teacher created successfully.');
              void this.router.navigate(['/dashboard/user-management']);
              this.showAddUser.set(false);
              this.getUsers();
              form.resetForm();
              this.newUserRole = '';
              this.qualifications = [];
              this.getDashboardData();
            },
            error: (err) => {
              this.isSubmitting.set(false);
              this.flashService.failure(
                err?.error?.message ?? 'Error creating teacher. Try again.',
              );
            },
          });
        } else {
          this.isSubmitting.set(false);
          this.flashService.success('User created successfully.');
          void this.router.navigate(['/dashboard/user-management']);
          this.showAddUser.set(false);
          this.getUsers();
          form.resetForm();
          this.newUserRole = '';
          this.getDashboardData();
        }
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.flashService.failure(err?.error?.message ?? 'User creation failed. Try again');
      },
    });
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService.getDashboardData(`${ApiEndpoints.url}/dashboard/user-stats`).subscribe({
      next: (res) => {
        this.dashboardData.set(res.payload);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
        );
      },
    });
  }
}
